import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../../../../firebase'; 
import type { Application } from '../../../types/application';
import { AlertState, AlertLog, TemplateType } from '../types';
import { TEMPLATE_OPTIONS, API_BASE_URL } from '../constants';
import { formatDeviceNames } from '../utils';
import { biztalkTemplates } from '../../../../../../verify-functions/src/utils/biztalkTemplates';

export const useAlertManagement = (application: Application | null) => {
  // 🔔 알림톡 상태 관리
  const [alertState, setAlertState] = useState<AlertState>({
    selectedType: '',
    repairReason: '',
    returnTrackingNumber: '',
    sending: false,
    confirmDialog: false,
    errorMessage: '',
    snackbar: false,
    history: [],
    historyLoading: false,
  });

  // 📊 알림톡 히스토리 실시간 로딩
  useEffect(() => {
    if (!application?.id) return;

    setAlertState(prev => ({ ...prev, historyLoading: true }));
    const alertsQuery = query(
      collection(db, 'alerts_log'),
      where('application_id', '==', application.id)
    );

    const unsubscribe = onSnapshot(
      alertsQuery,
      (snapshot) => {
        const logs: AlertLog[] = [];
        snapshot.forEach((doc) => {
          logs.push({ ...doc.data() } as AlertLog);
        });
        // 최신순으로 정렬
        logs.sort((a, b) => {
          const aTime = a.sent_at?.seconds || (a.sent_at as any)?.seconds || 0;
          const bTime = b.sent_at?.seconds || (b.sent_at as any)?.seconds || 0;
          return bTime - aTime;
        });
        setAlertState(prev => ({ 
          ...prev, 
          history: logs, 
          historyLoading: false 
        }));
      },
      (error) => {
        console.error('Failed to load alert history:', error);
        setAlertState(prev => ({ ...prev, historyLoading: false }));
      }
    );

    return () => unsubscribe();
  }, [application?.id]);

  // 🔔 알림톡 미리보기 메시지
  const alertPreviewMessage = useMemo(() => {
    if (!alertState.selectedType || !application) return '';
    const template = biztalkTemplates[alertState.selectedType];
    if (!template) return '';

    const baseParams = {
      userName: application.user_name,
      deviceName: formatDeviceNames(application.devices),
      applyDate: new Date(application.created_at?.seconds * 1000).toLocaleDateString('ko-KR'),
    };

    if (alertState.selectedType === '가격안내') {
      const priceParams = {
        ...baseParams,
        price: application.price_offer?.toLocaleString() || '0',
        application_id: application.id,
      };
      return template.getMessage(priceParams);
    }
    
    if (alertState.selectedType === '매입 불가') {
      return template.getMessage({ 
        ...baseParams, 
        reason: alertState.repairReason || '[매입 불가 사유를 입력하세요]',
        returnTrackingNumber: alertState.returnTrackingNumber || '처리 중'
      });
    }

    if (alertState.selectedType === '반송') {
      return template.getMessage({ 
        ...baseParams, 
        returnTrackingNumber: alertState.returnTrackingNumber || '[반송장번호를 입력하세요]'
      });
    }

    // 🆕 정보입력요청 (거래처 전용)
    if (alertState.selectedType === '정보입력요청') {
      return template.getMessage(baseParams);
    }
    
    return template.getMessage(baseParams);
  }, [alertState.selectedType, application, alertState.repairReason, alertState.returnTrackingNumber]);

  // 🔔 알림톡 폼 유효성 검사
  const isAlertFormValid = useMemo(() => {
    if (!alertState.selectedType) return false;
    if ((alertState.selectedType === '매입 불가') && !alertState.repairReason.trim()) return false;
    if (alertState.selectedType === '반송' && !alertState.returnTrackingNumber?.trim()) return false;
    return true;
  }, [alertState.selectedType, alertState.repairReason, alertState.returnTrackingNumber]);

  // 🔔 템플릿 선택 핸들러 (내부용)
  const selectTemplate = useCallback((templateType: TemplateType | '') => {
    setAlertState(prev => ({
      ...prev,
      selectedType: templateType,
      errorMessage: '',
      repairReason: (templateType !== '매입 불가') ? '' : (application?.deduction_reason || prev.repairReason),
      returnTrackingNumber: (templateType !== '매입 불가' && templateType !== '반송') ? '' : prev.returnTrackingNumber
    }));
  }, [application?.deduction_reason]);

  // 🔔 템플릿 선택 핸들러 (SelectChangeEvent용)
  const handleTemplateSelect = useCallback((e: any) => {
    const value = typeof e === 'string' ? e : e.target?.value || '';
    selectTemplate(value as TemplateType | '');
  }, [selectTemplate]);

  // 🔧 차감 사유 변경 핸들러
  const handleRepairReasonChange = useCallback((e: any) => {
    const value = typeof e === 'string' ? e : e.target?.value || '';
    setAlertState(prev => ({ ...prev, repairReason: value }));
  }, []);

  // 📦 반송장번호 변경 핸들러
  const handleReturnTrackingChange = useCallback((e: any) => {
    const value = typeof e === 'string' ? e : e.target?.value || '';
    setAlertState(prev => ({ ...prev, returnTrackingNumber: value }));
  }, []);

  // 📤 전송 시작 핸들러
  const handleSendClick = useCallback(() => {
    if (!isAlertFormValid) {
      setAlertState(prev => ({ ...prev, errorMessage: '필수 항목을 모두 입력해주세요.' }));
      return;
    }
    const selectedOption = TEMPLATE_OPTIONS.find(opt => opt.value === alertState.selectedType);
    if (selectedOption?.disabled) {
      setAlertState(prev => ({ ...prev, errorMessage: '해당 알림 서비스는 현재 준비 중입니다.' }));
      return;
    }
    setAlertState(prev => ({ ...prev, confirmDialog: true }));
  }, [isAlertFormValid, alertState.selectedType]);

  // ✅ 전송 확인 핸들러
  const handleConfirmSend = useCallback(async () => {
    if (!application?.id || !alertState.selectedType) return;
    
    setAlertState(prev => ({ 
      ...prev, 
      sending: true, 
      confirmDialog: false 
    }));
    
    try {
      const isPriceNotice = alertState.selectedType === '가격안내';

      if (isPriceNotice) {
        // 가격안내는 공용 per-device API 호출로만 처리 (템플릿 조회/타이틀/링크 불필요)
        const { sendAlimtalkPriceNotice } = await import('../../../../../utils/alerts');
        const result = await sendAlimtalkPriceNotice(application.id, false);
        console.log('✅ per-device 가격안내 전송 결과:', result);
      } else {
        const template = biztalkTemplates[alertState.selectedType as TemplateType];
        if (!template) throw new Error('템플릿을 찾을 수 없습니다.');

        // AlertDetailPanel과 동일한 파라미터 구조 사용 (non-price only)
        const deviceName = formatDeviceNames(application.devices);
        const applyDate = new Date(application.created_at?.seconds * 1000).toLocaleDateString('ko-KR');

        const payload: any = {
          application_id: application.id,
          user_name: application.user_name,
          phone_number: application.phone_number,
          device_name: deviceName,
          apply_date: applyDate,
          event: alertState.selectedType,
          title: template.getTitle?.({
            userName: application.user_name,
            deviceName: deviceName,
            applyDate: applyDate,
          }) || '',
          link: template.link || null,
        };

        // 매입 불가 템플릿 특수 처리
        if (alertState.selectedType === '매입 불가') {
          payload.reason = alertState.repairReason.trim();
          payload.return_tracking_number = alertState.returnTrackingNumber?.trim() || '';
        }

        // 반송 템플릿 특수 처리
        if (alertState.selectedType === '반송') {
          payload.return_tracking_number = alertState.returnTrackingNumber?.trim() || '';
        }

        // 🆕 정보입력요청 템플릿 특수 처리 (거래처 전용)
        if (alertState.selectedType === '정보입력요청') {
          // 기존 토큰이 있으면 재사용, 없으면 새로 생성해야 함
          if (application.info_token_id) {
            payload.token = application.info_token_id;
          } else {
            // 토큰이 없으면 API에서 자동 생성하도록 처리
            // manualApplication API를 직접 호출하지 않고 alerts/send에서 처리
            payload.token = ''; // API에서 새 토큰 생성 필요 표시
          }
        }

        console.log('📤 전송 파라미터 (non-price):', payload);
        console.log('🔔 선택된 템플릿:', alertState.selectedType);

        const response = await fetch(`${API_BASE_URL}/api/alerts/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          console.error('🚨 HTTP 응답 오류:', response.status, response.statusText);
          const errorData = await response.json().catch(() => ({}));
          console.error('🔍 서버 오류 데이터:', errorData);
          throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: 서버 오류가 발생했습니다.`);
        }

        const result = await response.json();
        console.log('✅ 알림톡 전송 성공 (non-price):', result);
      }

      // 전송 성공 시 상태 초기화
      setAlertState(prev => ({
        ...prev,
        snackbar: true,
        selectedType: '',
        repairReason: '',
        returnTrackingNumber: '',
        errorMessage: '',
      }));
      
    } catch (error) {
      console.error('❌ 알림톡 전송 실패:', error);
      const errorMessage = error instanceof Error ? error.message : '알림톡 전송에 실패했습니다. 다시 시도해주세요.';
      setAlertState(prev => ({ ...prev, errorMessage }));
    } finally {
      setAlertState(prev => ({ ...prev, sending: false }));
    }
  }, [application, alertState.selectedType, alertState.repairReason, alertState.returnTrackingNumber]);

  // 🎛️ UI 상태 변경 핸들러들
  const setConfirmDialog = useCallback((open: boolean) => {
    setAlertState(prev => ({ ...prev, confirmDialog: open }));
  }, []);

  const setSnackbar = useCallback((open: boolean) => {
    setAlertState(prev => ({ ...prev, snackbar: open }));
  }, []);

  return {
    alertState,
    alertPreviewMessage,
    isAlertFormValid,
    handleTemplateSelect,
    handleRepairReasonChange,
    handleReturnTrackingChange,
    handleSendClick,
    handleConfirmSend,
    setConfirmDialog,
    setSnackbar,
  };
}; 